import {
  bool,
  string,
  oneOf,
  oneOfType,
  func,
  array,
  node,
  object,
  number,
} from 'prop-types'
import { Fragment } from 'react'
import Icon from '../../Icon'
import Loader from '../../Loader'
import { renderIcon, kuiperClassNames, noPropagate } from '../../utils'

const arrowClickHandler = (disabled, focused, onFocus, onBlur) =>
  noPropagate(() => {
    if (disabled) {
      return false
    }
    if (focused) {
      return onBlur()
    }
    return onFocus()
  })

const leftIcon = (icon, hasMenu, position, options) => {
  if (icon && (hasMenu || position === 'left')) {
    return renderIcon(icon, options)
  }

  return null
}

const rightIcon = (icon, hasMenu, position, options) => {
  if (icon && position === 'right' && !hasMenu) {
    return renderIcon(icon, options)
  }

  return null
}

const labelText = (label) => {
  // Sometimes the label needs to be the number 0
  if (label || label === 0) {
    return <div className={kuiperClassNames('label-text')}>{label}</div>
  }
  return null
}

const arrow = ({
  arrowIcon = 'triangle-sm-s',
  arrowIconHeight = 5,
  arrowIconWidth = 7,
  hasMenu,
  noArrow,
  onArrowClick,
}) => {
  if (!hasMenu || noArrow) {
    return null
  }
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={kuiperClassNames('button-menu-arrow')}
      onClick={onArrowClick}
    >
      <Icon
        className={kuiperClassNames('menu-arrow')}
        glyph={arrowIcon}
        width={arrowIconWidth}
        height={arrowIconHeight}
        ariaHidden={true}
      />
    </div>
  )
}

const renderLabel = (
  disabled,
  focused,
  hasMenu,
  hideLabelText,
  icon,
  iconOptions,
  isLoading,
  arrowIcon,
  arrowIconWidth,
  arrowIconHeight,
  label,
  noArrow,
  onBlur,
  onFocus,
  position,
  theme
) => {
  if (isLoading) {
    return <Loader dotSize="small" theme={theme} />
  }
  const iconOptionsWithAriaMode = iconOptions || {}
  if (!iconOptionsWithAriaMode.ariaMode) {
    if (hideLabelText) {
      iconOptionsWithAriaMode.ariaMode = 'presentational'
    } else {
      iconOptionsWithAriaMode.ariaMode = 'hidden'
    }
  }

  return (
    <Fragment>
      {leftIcon(icon, hasMenu, position, iconOptionsWithAriaMode)}
      {labelText(label)}
      {rightIcon(icon, hasMenu, position, iconOptionsWithAriaMode)}
      {arrow({
        hasMenu,
        noArrow,
        onArrowClick: arrowClickHandler(disabled, focused, onFocus, onBlur),
        arrowIcon,
        arrowIconWidth,
        arrowIconHeight,
      })}
    </Fragment>
  )
}

const ButtonLabel = ({
  disabled,
  focused,
  hasMenu,
  hideLabelText,
  icon,
  iconOptions,
  isLoading,
  arrowIcon,
  arrowIconWidth,
  arrowIconHeight,
  label,
  noArrow,
  onBlur,
  onFocus,
  position,
  theme,
}) => (
  <div
    className={kuiperClassNames('label', { 'hide-label': hideLabelText })}
    tabIndex={-1}
  >
    {renderLabel(
      disabled,
      focused,
      hasMenu,
      hideLabelText,
      icon,
      iconOptions,
      isLoading,
      arrowIcon,
      arrowIconWidth,
      arrowIconHeight,
      label,
      noArrow,
      onBlur,
      onFocus,
      position,
      theme
    )}
  </div>
)

ButtonLabel.propTypes = {
  arrowIcon: string,
  arrowIconHeight: oneOfType([string, number]),
  arrowIconWidth: oneOfType([string, number]),
  disabled: bool,
  focused: bool,
  hasMenu: bool,
  hideLabelText: bool,
  icon: string,
  iconOptions: object,
  isLoading: bool,
  label: oneOfType([node, array, string]),
  noArrow: bool,
  onBlur: func,
  onFocus: func,
  position: oneOf(['left', 'right']),
  theme: string,
}

export default ButtonLabel
